import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
	FiArrowLeft,
	FiRefreshCw,
	FiTruck,
	FiSlash,
	FiPackage,
	FiRotateCcw,
	FiFileText,
	FiClock,
} from "react-icons/fi";
import { Modal } from "../../Components/Modal";
import { usePermissions } from "../../Auth/PermissionsContext";
import {
	shopClient,
	eurCents,
	fmtDate,
	fmtDateTime,
	type ShopOrderDetail,
	type ShopOrderLine,
	type OrderStatus,
} from "./shopClient";

const STATUS_LABELS: Record<OrderStatus, string> = {
	Pending: "En attente",
	Paid: "Payée",
	Fulfilled: "Expédiée",
	Refunded: "Remboursée",
	Cancelled: "Annulée",
};

type Tone = "emerald" | "amber" | "slate" | "rose";

const STATUS_TONE: Record<OrderStatus, Tone> = {
	Paid: "emerald",
	Fulfilled: "emerald",
	Pending: "amber",
	Refunded: "slate",
	Cancelled: "rose",
};

const TONE_CLASSES: Record<Tone, string> = {
	emerald: "bg-emerald-50 text-emerald-700",
	amber: "bg-amber-50 text-amber-700",
	slate: "bg-slate-100 text-slate-600",
	rose: "bg-rose-50 text-rose-600",
};

function StatusBadge({ status }: { status: OrderStatus }) {
	const tone = STATUS_TONE[status] ?? "slate";
	return (
		<span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASSES[tone]}`}>
			{STATUS_LABELS[status] ?? status}
		</span>
	);
}

const STATUS_OPTIONS: OrderStatus[] = ["Pending", "Paid", "Fulfilled", "Refunded", "Cancelled"];

function truncateMiddle(s?: string | null, head = 14, tail = 6): string {
	if (!s) return "—";
	if (s.length <= head + tail + 1) return s;
	return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

export default function OrderDetail() {
	const { id } = useParams<{ id: string }>();
	const { can } = usePermissions();
	const canWrite = can("feature:orders:write");

	const [order, setOrder] = useState<ShopOrderDetail | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	// Action state
	const [nextStatus, setNextStatus] = useState<OrderStatus>("Paid");
	const [busy, setBusy] = useState<boolean>(false);

	// Modals
	const [shipOpen, setShipOpen] = useState(false);
	const [carrier, setCarrier] = useState("");
	const [trackingNumber, setTrackingNumber] = useState("");

	const [refundOpen, setRefundOpen] = useState(false);
	const [refundAmount, setRefundAmount] = useState("");

	const [cancelOpen, setCancelOpen] = useState(false);
	const [cancelReason, setCancelReason] = useState("");

	const [noteOpen, setNoteOpen] = useState(false);
	const [noteText, setNoteText] = useState("");

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await shopClient.orders.get(id);
			setOrder(res);
			setNextStatus(res.status);
		} catch (e: any) {
			setError(e?.message ?? "Erreur");
			setOrder(null);
		} finally {
			setLoading(false);
		}
	}, [id]);

	useEffect(() => {
		void load();
	}, [load]);

	const remainingCents = useMemo(
		() => (order ? order.totalCents - order.refundedTotalCents : 0),
		[order]
	);

	const sortedLines = useMemo<ShopOrderLine[]>(() => {
		if (!order) return [];
		const products = order.lines.filter((l) => l.kind !== "Consigne");
		const consignes = order.lines.filter((l) => l.kind === "Consigne");
		return [...products, ...consignes];
	}, [order]);

	const applyResult = (updated: ShopOrderDetail, message: string) => {
		setOrder(updated);
		setNextStatus(updated.status);
		toast.success(message);
	};

	const runAction = async (fn: () => Promise<ShopOrderDetail>, successMsg: string, onDone?: () => void) => {
		setBusy(true);
		try {
			const updated = await fn();
			applyResult(updated, successMsg);
			onDone?.();
		} catch (e: any) {
			toast.error(e?.message ?? "Erreur");
		} finally {
			setBusy(false);
		}
	};

	const onApplyStatus = () =>
		runAction(() => shopClient.orders.setStatus(id, nextStatus), "Statut mis à jour");

	const onShip = () =>
		runAction(
			() => shopClient.orders.ship(id, carrier.trim() || undefined, trackingNumber.trim() || undefined),
			"Commande marquée comme expédiée",
			() => setShipOpen(false)
		);

	const onRefund = () => {
		const euros = parseFloat(refundAmount.replace(",", "."));
		const cents = Number.isFinite(euros) ? Math.round(euros * 100) : null;
		return runAction(
			() => shopClient.orders.refund(id, cents),
			"Remboursement effectué",
			() => setRefundOpen(false)
		);
	};

	const onCancel = () =>
		runAction(
			() => shopClient.orders.cancel(id, cancelReason.trim() || undefined),
			"Commande annulée",
			() => setCancelOpen(false)
		);

	const onNote = () =>
		runAction(
			() => shopClient.orders.note(id, noteText.trim()),
			"Note ajoutée",
			() => {
				setNoteText("");
				setNoteOpen(false);
			}
		);

	const openRefund = () => {
		setRefundAmount((remainingCents / 100).toFixed(2));
		setRefundOpen(true);
	};

	if (loading) {
		return (
			<div className="min-h-full p-6">
				<div className="mx-auto w-full max-w-6xl">
					<div className="text-slate-400">Chargement…</div>
				</div>
			</div>
		);
	}

	if (error || !order) {
		return (
			<div className="min-h-full p-6">
				<div className="mx-auto w-full max-w-6xl space-y-6">
					<Link
						to="/orders"
						className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
					>
						<FiArrowLeft />
						Retour aux commandes
					</Link>
					<div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
						{error ?? "Commande introuvable"}
					</div>
				</div>
			</div>
		);
	}

	const o = order;

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-6">
				<Link
					to="/orders"
					className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
				>
					<FiArrowLeft />
					Retour aux commandes
				</Link>

				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Boutique</p>
						<h1 className="font-mono text-2xl font-semibold text-slate-900">
							Commande #{o.id.slice(0, 8).toUpperCase()}
						</h1>
						<p className="mt-1 text-sm text-slate-500">Créée le {fmtDate(o.createdAt)}</p>
					</div>
					<div className="ml-auto flex flex-wrap items-center gap-2">
						<StatusBadge status={o.status} />
						{o.refundedTotalCents > 0 && (
							<span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-600">
								Remboursé {eurCents(o.refundedTotalCents, o.currency)}
							</span>
						)}
						<button
							type="button"
							onClick={() => void load()}
							disabled={busy}
							className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
						>
							<FiRefreshCw className={busy ? "animate-spin" : ""} />
							Actualiser
						</button>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
					{/* LEFT */}
					<div className="space-y-6 lg:col-span-2">
						{/* Articles */}
						<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
							<div className="border-b border-slate-100 px-5 py-3">
								<h2 className="text-sm font-semibold text-slate-700">Articles</h2>
							</div>
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-slate-100 text-sm">
									<thead className="bg-slate-50">
										<tr>
											<th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-widest text-slate-500">Désignation</th>
											<th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-widest text-slate-500">Qté</th>
											<th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-widest text-slate-500">PU</th>
											<th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-widest text-slate-500">Total</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-slate-100">
										{sortedLines.map((l) => {
											const isConsigne = l.kind === "Consigne";
											return (
												<tr key={l.id} className={isConsigne ? "bg-slate-50/40" : ""}>
													<td className="px-5 py-3">
														<div className={isConsigne ? "text-slate-500" : "font-medium text-slate-800"}>
															{l.title}
															{isConsigne && (
																<span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
																	Consigne
																</span>
															)}
														</div>
														{l.reference && (
															<div className="text-xs text-slate-400">Réf. {l.reference}</div>
														)}
													</td>
													<td className="px-5 py-3 text-right text-slate-600">{l.quantity}</td>
													<td className="px-5 py-3 text-right text-slate-600">
														{eurCents(l.unitPriceCents, o.currency)}
													</td>
													<td className="px-5 py-3 text-right font-medium text-slate-800">
														{eurCents(l.lineTotalCents, o.currency)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							<div className="space-y-1.5 border-t border-slate-100 px-5 py-4 text-sm">
								<div className="flex items-center justify-between text-slate-600">
									<span>Sous-total</span>
									<span>{eurCents(o.subtotalCents, o.currency)}</span>
								</div>
								<div className="flex items-center justify-between text-slate-600">
									<span>Consignes</span>
									<span>{eurCents(o.consigneTotalCents, o.currency)}</span>
								</div>
								<div className="flex items-center justify-between border-t border-slate-100 pt-1.5 text-base font-semibold text-slate-900">
									<span>Total</span>
									<span>{eurCents(o.totalCents, o.currency)}</span>
								</div>
								{o.refundedTotalCents > 0 && (
									<div className="flex items-center justify-between text-rose-600">
										<span>Remboursé</span>
										<span>− {eurCents(o.refundedTotalCents, o.currency)}</span>
									</div>
								)}
							</div>
						</div>

						{/* Historique */}
						<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
							<div className="border-b border-slate-100 px-5 py-3">
								<h2 className="text-sm font-semibold text-slate-700">Historique</h2>
							</div>
							<div className="px-5 py-4">
								{o.events.length === 0 ? (
									<div className="py-6 text-center text-slate-400">Aucun événement</div>
								) : (
									<ol className="space-y-4">
										{o.events.map((ev) => (
											<li key={ev.id} className="flex gap-3">
												<span className="mt-1 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-slate-100 text-slate-500">
													<FiClock size={12} />
												</span>
												<div className="min-w-0">
													<p className="text-sm text-slate-800">{ev.message}</p>
													<p className="text-xs text-slate-400">
														{ev.actor ? `${ev.actor} · ` : ""}
														{fmtDateTime(ev.createdAt)}
													</p>
												</div>
											</li>
										))}
									</ol>
								)}
							</div>
						</div>
					</div>

					{/* RIGHT */}
					<div className="space-y-4 lg:col-span-1">
						{canWrite && (
							<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
								<div className="border-b border-slate-100 px-5 py-3">
									<h2 className="text-sm font-semibold text-slate-700">Actions</h2>
								</div>
								<div className="space-y-3 px-5 py-4">
									<div className="flex items-center gap-2">
										<select
											value={nextStatus}
											onChange={(e) => setNextStatus(e.target.value as OrderStatus)}
											className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
										>
											{STATUS_OPTIONS.map((s) => (
												<option key={s} value={s}>
													{STATUS_LABELS[s]}
												</option>
											))}
										</select>
										<button
											type="button"
											onClick={onApplyStatus}
											disabled={busy || nextStatus === o.status}
											className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
										>
											Appliquer
										</button>
									</div>

									<button
										type="button"
										onClick={() => {
											setCarrier(o.trackingCarrier ?? "");
											setTrackingNumber(o.trackingNumber ?? "");
											setShipOpen(true);
										}}
										disabled={busy}
										className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
									>
										<FiTruck />
										Marquer expédiée
									</button>

									<button
										type="button"
										onClick={openRefund}
										disabled={busy || remainingCents <= 0}
										className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
									>
										<FiRotateCcw />
										Rembourser
									</button>

									<button
										type="button"
										onClick={() => {
											setNoteText("");
											setNoteOpen(true);
										}}
										disabled={busy}
										className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 disabled:opacity-50"
									>
										<FiFileText />
										Ajouter une note
									</button>

									<button
										type="button"
										onClick={() => {
											setCancelReason("");
											setCancelOpen(true);
										}}
										disabled={busy}
										className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
									>
										<FiSlash />
										Annuler la commande
									</button>
								</div>
							</div>
						)}

						{/* Client */}
						<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
							<div className="border-b border-slate-100 px-5 py-3">
								<h2 className="text-sm font-semibold text-slate-700">Client</h2>
							</div>
							<div className="space-y-1 px-5 py-4 text-sm">
								{o.userId ? (
									<Link
										to={"/customers/" + o.userId}
										className="font-medium text-slate-900 hover:underline"
									>
										{o.customerName || o.customerEmail || "Client"}
									</Link>
								) : (
									<div className="flex items-center gap-2">
										<span className="text-slate-800">{o.customerEmail || "—"}</span>
										<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
											Invité
										</span>
									</div>
								)}
								{o.userId && o.customerEmail && (
									<div className="text-xs text-slate-400">{o.customerEmail}</div>
								)}
								{o.anonymizedAt && (
									<div className="mt-2 text-xs text-amber-700">Données anonymisées (RGPD)</div>
								)}
							</div>
						</div>

						{/* Livraison */}
						<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
							<div className="border-b border-slate-100 px-5 py-3">
								<h2 className="text-sm font-semibold text-slate-700">Livraison</h2>
							</div>
							<div className="space-y-1 px-5 py-4 text-sm text-slate-700">
								{o.shippingName && <div className="font-medium text-slate-900">{o.shippingName}</div>}
								{o.shippingLine1 && <div>{o.shippingLine1}</div>}
								{o.shippingLine2 && <div>{o.shippingLine2}</div>}
								{(o.shippingPostalCode || o.shippingCity) && (
									<div>
										{[o.shippingPostalCode, o.shippingCity].filter(Boolean).join(" ")}
									</div>
								)}
								{o.shippingCountry && <div>{o.shippingCountry}</div>}
								{!o.shippingName &&
									!o.shippingLine1 &&
									!o.shippingCity &&
									!o.shippingPostalCode && (
										<div className="text-slate-400">Aucune adresse de livraison</div>
									)}
								{(o.trackingCarrier || o.trackingNumber) && (
									<div className="mt-2 flex items-center gap-2 border-t border-slate-100 pt-2 text-slate-600">
										<FiTruck className="flex-none" />
										<span>
											{o.trackingCarrier || "Transporteur"}
											{o.trackingNumber ? ` · ${o.trackingNumber}` : ""}
										</span>
									</div>
								)}
							</div>
						</div>

						{/* Paiement */}
						<div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
							<div className="border-b border-slate-100 px-5 py-3">
								<h2 className="text-sm font-semibold text-slate-700">Paiement</h2>
							</div>
							<div className="space-y-2 px-5 py-4 text-sm">
								<div>
									<p className="text-xs uppercase tracking-widest text-slate-400">Session</p>
									<p className="font-mono text-xs text-slate-500" title={o.stripeSessionId}>
										{truncateMiddle(o.stripeSessionId)}
									</p>
								</div>
								<div>
									<p className="text-xs uppercase tracking-widest text-slate-400">Payment Intent</p>
									<p className="font-mono text-xs text-slate-500" title={o.stripePaymentIntentId ?? ""}>
										{truncateMiddle(o.stripePaymentIntentId)}
									</p>
								</div>
								{o.internalNotes && (
									<div className="border-t border-slate-100 pt-2">
										<p className="text-xs uppercase tracking-widest text-slate-400">Notes internes</p>
										<p className="whitespace-pre-wrap text-sm text-slate-600">{o.internalNotes}</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Ship modal */}
			<Modal show={shipOpen} onHide={() => setShipOpen(false)} size="md" centered>
				<Modal.Header closeButton>
					<Modal.Title>Marquer comme expédiée</Modal.Title>
				</Modal.Header>
				<Modal.Body className="space-y-4">
					<label className="block text-sm">
						<span className="mb-1 block font-medium text-slate-700">Transporteur</span>
						<input
							value={carrier}
							onChange={(e) => setCarrier(e.target.value)}
							placeholder="Ex. Chronopost, DPD…"
							className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
						/>
					</label>
					<label className="block text-sm">
						<span className="mb-1 block font-medium text-slate-700">Numéro de suivi</span>
						<input
							value={trackingNumber}
							onChange={(e) => setTrackingNumber(e.target.value)}
							placeholder="Numéro de tracking"
							className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
						/>
					</label>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShipOpen(false)}
						className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={onShip}
						disabled={busy}
						className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
					>
						<FiPackage />
						Confirmer l'expédition
					</button>
				</Modal.Footer>
			</Modal>

			{/* Refund modal */}
			<Modal show={refundOpen} onHide={() => setRefundOpen(false)} size="md" centered>
				<Modal.Header closeButton>
					<Modal.Title>Rembourser la commande</Modal.Title>
				</Modal.Header>
				<Modal.Body className="space-y-4">
					<p className="text-sm text-slate-500">
						Restant remboursable : {eurCents(remainingCents, o.currency)}
					</p>
					<label className="block text-sm">
						<span className="mb-1 block font-medium text-slate-700">Montant (€)</span>
						<input
							type="number"
							min="0"
							step="0.01"
							value={refundAmount}
							onChange={(e) => setRefundAmount(e.target.value)}
							className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
						/>
					</label>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setRefundOpen(false)}
						className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={onRefund}
						disabled={busy}
						className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
					>
						<FiRotateCcw />
						Rembourser
					</button>
				</Modal.Footer>
			</Modal>

			{/* Cancel modal */}
			<Modal show={cancelOpen} onHide={() => setCancelOpen(false)} size="md" centered>
				<Modal.Header closeButton>
					<Modal.Title>Annuler la commande</Modal.Title>
				</Modal.Header>
				<Modal.Body className="space-y-4">
					<p className="text-sm text-slate-500">
						Cette action annule la commande. Indiquez éventuellement un motif.
					</p>
					<label className="block text-sm">
						<span className="mb-1 block font-medium text-slate-700">Motif</span>
						<textarea
							value={cancelReason}
							onChange={(e) => setCancelReason(e.target.value)}
							rows={3}
							placeholder="Motif de l'annulation (optionnel)"
							className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
						/>
					</label>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setCancelOpen(false)}
						className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
					>
						Retour
					</button>
					<button
						type="button"
						onClick={onCancel}
						disabled={busy}
						className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
					>
						<FiSlash />
						Annuler la commande
					</button>
				</Modal.Footer>
			</Modal>

			{/* Note modal */}
			<Modal show={noteOpen} onHide={() => setNoteOpen(false)} size="md" centered>
				<Modal.Header closeButton>
					<Modal.Title>Ajouter une note</Modal.Title>
				</Modal.Header>
				<Modal.Body className="space-y-4">
					<label className="block text-sm">
						<span className="mb-1 block font-medium text-slate-700">Note interne</span>
						<textarea
							value={noteText}
							onChange={(e) => setNoteText(e.target.value)}
							rows={4}
							placeholder="Votre note…"
							className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
						/>
					</label>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setNoteOpen(false)}
						className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={onNote}
						disabled={busy || noteText.trim().length === 0}
						className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
					>
						<FiFileText />
						Enregistrer
					</button>
				</Modal.Footer>
			</Modal>
		</div>
	);
}
